import { useState, useEffect, useRef, useMemo } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { usePrepTime, usePublicSettings } from "@/lib/settings";
import { useCart } from "@/contexts/CartContext";
import ProductDialog from "@/components/ProductDialog";
import { Input } from "@/components/ui/input";
import { Search, ShoppingBag } from "lucide-react";
import { toast } from "sonner";
import { imgUrl } from "@/lib/imageUrl";

export default function Order() {
  const [data, setData] = useState({ categories: [], products: [], addons: [] });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [activeCat, setActiveCat] = useState("");
  const [openProduct, setOpenProduct] = useState(null);
  const { items, count, subtotal } = useCart();
  const prepTime = usePrepTime();
  const settings = usePublicSettings();
  const sectionsRef = useRef({});

  // Vérifier si aujourd'hui est un jour de fermeture (vacances)
  const isClosedToday = (() => {
    if (!settings?.closed_dates?.length) return false;
    const today = new Intl.DateTimeFormat("en-CA", { timeZone: "Europe/Zurich" }).format(new Date());
    return settings.closed_dates.includes(today);
  })();

  // Prochaine date de réouverture (première date après aujourd'hui qui n'est PAS fermée)
  const nextOpenDate = (() => {
    if (!isClosedToday) return null;
    const closed = new Set(settings.closed_dates || []);
    const d = new Date();
    for (let i = 1; i <= 30; i++) {
      d.setDate(d.getDate() + 1);
      const iso = new Intl.DateTimeFormat("en-CA", { timeZone: "Europe/Zurich" }).format(d);
      if (!closed.has(iso)) return iso;
    }
    return null;
  })();

  useEffect(() => {
    api.get("/menu/data")
      .then(({ data }) => {
        setData(data);
        if (data.categories[0]) setActiveCat(data.categories[0].id);
      })
      .catch(() => toast.error("Impossible de charger le menu"))
      .finally(() => setLoading(false));
  }, []);

  const productsByCat = useMemo(() => {
    const filtered = data.products.filter(p => p.name.toLowerCase().includes(search.toLowerCase()) || p.description.toLowerCase().includes(search.toLowerCase()));
    const map = {};
    data.categories.forEach(c => { map[c.id] = filtered.filter(p => p.category_id === c.id); });
    return map;
  }, [data, search]);

  const scrollTo = (catId) => {
    setActiveCat(catId);
    const el = sectionsRef.current[catId];
    if (el) window.scrollTo({ top: el.offsetTop - 200, behavior: "smooth" });
  };

  return (
    <div data-testid="order-page" className="pt-24 pb-32 bg-background">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        {isClosedToday && (
          <div data-testid="closed-banner" className="mt-24 mb-4 p-5 bg-destructive/10 border-2 border-destructive rounded-sm text-center">
            <p className="uppercase tracking-[0.2em] text-xs text-destructive font-semibold mb-2">Restaurant fermé aujourd&apos;hui</p>
            <p className="text-foreground text-base">
              Nous sommes en <strong>congés / fermeture exceptionnelle</strong>.
              {nextOpenDate && (
                <> Réouverture prévue le <strong>{new Date(nextOpenDate + "T00:00:00").toLocaleDateString("fr-CH", { weekday: "long", day: "2-digit", month: "long" })}</strong>.</>
              )}
            </p>
            <p className="text-sm text-muted-foreground mt-2">La prise de commande est temporairement désactivée.</p>
          </div>
        )}
        {/* Heading */}
        <div className="py-12">
          <p className="uppercase tracking-[0.3em] text-xs text-primary mb-4">À emporter</p>
          <h1 className="font-display text-5xl lg:text-6xl text-foreground mb-3">Prendre à l&apos;emporter</h1>
          <p className="text-muted-foreground text-lg max-w-2xl">
            <strong className="text-foreground">C'est ici que les prix sont les moins chers.</strong> En commandant directement sur notre site, vous nous soutenez à 100 % — sans intermédiaire ni commission.
          </p>
          <p className="text-muted-foreground text-sm max-w-2xl mt-2">Préparé fraîchement, prêt en environ {prepTime} minute{prepTime > 1 ? "s" : ""}. Paiement sur place (Twint, cash, carte).</p>
        </div>

        {/* Search */}
        <div className="relative mb-8 max-w-md">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground" size={18} />
          <Input
            data-testid="product-search"
            placeholder="Rechercher un plat…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-12 h-12 bg-white border-border rounded-sm"
          />
        </div>

        {/* Sticky categories */}
        <div data-testid="category-nav" className="sticky top-20 z-30 bg-background/95 backdrop-blur py-4 border-b border-border -mx-6 px-6 lg:-mx-10 lg:px-10 mb-8">
          <div className="flex gap-2 overflow-x-auto scrollbar-hide">
            {data.categories.map((c) => (
              <button
                key={c.id}
                data-testid={`cat-tab-${c.id}`}
                onClick={() => scrollTo(c.id)}
                className={`whitespace-nowrap px-5 py-2 rounded-full text-sm transition ${
                  activeCat === c.id ? "bg-primary text-white" : "bg-secondary text-foreground hover:bg-accent"
                }`}
              >
                {c.name}
              </button>
            ))}
          </div>
        </div>

        {/* Products by category */}
        {loading ? (
          <div className="text-center py-20 text-muted-foreground">Chargement du menu…</div>
        ) : (
          <div className="space-y-16">
            {data.categories.map((cat) => {
              const list = productsByCat[cat.id] || [];
              if (list.length === 0) return null;
              return (
                <section key={cat.id} ref={(el) => (sectionsRef.current[cat.id] = el)}>
                  <h2 className="font-display text-4xl text-foreground mb-8 flex items-center gap-4">
                    {cat.name}
                    <span className="flex-1 h-px bg-border" />
                  </h2>
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                    {list.map((p) => (
                      <ProductCard key={p.id} product={p} onClick={() => setOpenProduct(p)} />
                    ))}
                  </div>
                </section>
              );
            })}
          </div>
        )}
      </div>

      {/* Sticky cart bar */}
      {count > 0 && (
        <div data-testid="sticky-cart" className="fixed bottom-6 left-1/2 -translate-x-1/2 z-40 w-[calc(100%-2rem)] max-w-md">
          <Link
            to="/checkout"
            className="bg-primary text-white px-6 py-4 rounded-sm flex items-center justify-between shadow-2xl hover:bg-primary/90 transition"
            data-testid="view-cart-btn"
          >
            <span className="flex items-center gap-3">
              <ShoppingBag size={20} />
              <span className="font-medium">{count} {count > 1 ? "articles" : "article"}</span>
            </span>
            <span className="flex items-center gap-3">
              <span className="font-semibold">{subtotal.toFixed(2)} CHF</span>
              <span className="opacity-60">·</span>
              <span>Voir le panier →</span>
            </span>
          </Link>
        </div>
      )}

      {openProduct && (
        <ProductDialog
          product={openProduct}
          addons={data.addons.filter(a => (openProduct.addon_ids || []).includes(a.id))}
          onClose={() => setOpenProduct(null)}
        />
      )}
    </div>
  );
}

function ProductCard({ product, onClick }) {
  return (
    <button
      onClick={onClick}
      data-testid={`product-card-${product.id}`}
      className="group bg-white border border-border rounded-sm overflow-hidden text-left hover:border-primary transition-all hover:shadow-md"
    >
      <div className="aspect-square overflow-hidden bg-secondary">
        {product.image ? (
          <img src={imgUrl(product.image)} alt={product.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-muted-foreground font-display text-3xl">花</div>
        )}
      </div>
      <div className="p-3">
        <h3 className="font-display text-base leading-tight text-foreground line-clamp-1">{product.name}</h3>
        {product.description && <p className="text-xs text-muted-foreground leading-snug line-clamp-2 mt-1 min-h-[2rem]">{product.description}</p>}
        <div className="mt-2 text-primary font-semibold text-sm">{product.price.toFixed(2)} CHF</div>
      </div>
    </button>
  );
}
